import React from "react";
import { Box, BoxProps } from "@chakra-ui/react";

interface DoodleProps extends BoxProps {
  color?: string;
  strokeWidth?: number;
  width?: string | number;
  height?: string | number;
}

/**
 * Hand-drawn wavy underline for highlighting hero words and key phrases
 */
export const DoodleUnderline: React.FC<DoodleProps> = ({
  color = "#F59E0B",
  strokeWidth = 3,
  width = "100%",
  height = "14px",
  ...rest
}) => (
  <Box display="inline-block" w={width} h={height} {...rest}>
    <svg
      aria-hidden="true"
      viewBox="0 0 250 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "100%", overflow: "visible" }}
      preserveAspectRatio="none"
    >
      <path
        d="M2 14C35 4 68 18 102 10C136 3 175 16 210 8C224 5 238 9 248 7"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 17C50 11 88 19 122 14C158 8 195 17 232 12"
        stroke={color}
        strokeWidth={Math.max(1.5, strokeWidth - 1)}
        strokeLinecap="round"
        opacity="0.65"
      />
    </svg>
  </Box>
);

/**
 * Hand-drawn ellipse circle highlight for numbers or badges
 */
export const DoodleCircle: React.FC<DoodleProps> = ({
  color = "#E11D48",
  strokeWidth = 2.5,
  width = "100%",
  height = "100%",
  ...rest
}) => (
  <Box
    position="absolute"
    inset="-6px -10px"
    pointerEvents="none"
    zIndex={1}
    {...rest}
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 160 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "100%" }}
      preserveAspectRatio="none"
    >
      <path
        d="M25 45C10 38 12 18 35 10C72 0 135 4 148 24C160 42 135 62 90 66C40 70 5 55 18 30C23 20 40 14 62 12"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

/**
 * Hand-drawn curved arrow pointing to CTAs, inputs, or features
 */
export const DoodleArrow: React.FC<
  DoodleProps & { direction?: "down-right" | "down-left" | "up-right" }
> = ({
  color = "#0A3D2E",
  strokeWidth = 2.5,
  direction = "down-right",
  width = "60px",
  height = "50px",
  ...rest
}) => {
  const transform =
    direction === "down-left"
      ? "scaleX(-1)"
      : direction === "up-right"
      ? "scaleY(-1)"
      : "none";

  return (
    <Box
      display="inline-block"
      w={width}
      h={height}
      style={{ transform }}
      {...rest}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 80 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: "100%", height: "100%" }}
      >
        <path
          d="M8 8C28 10 58 18 64 42"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d="M45 42C53 44 63 44 65 43C65 40 64 30 63 24"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

/**
 * Hand-drawn 4-point sparkle
 */
export const DoodleSparkle: React.FC<DoodleProps & { size?: number }> = ({
  color = "#F59E0B",
  size = 24,
  ...rest
}) => (
  <Box display="inline-block" w={`${size}px`} h={`${size}px`} {...rest}>
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "100%" }}
    >
      <path d="M12 0C12.5 6.5 17.5 11.5 24 12C17.5 12.5 12.5 17.5 12 24C11.5 17.5 6.5 12.5 0 12C6.5 11.5 11.5 6.5 12 0Z" />
    </svg>
  </Box>
);

/**
 * Hand-drawn 5-point star
 */
export const DoodleStar: React.FC<DoodleProps & { size?: number }> = ({
  color = "#FBBF24",
  size = 24,
  strokeWidth = 2,
  ...rest
}) => (
  <Box display="inline-block" w={`${size}px`} h={`${size}px`} {...rest}>
    <svg
      aria-hidden="true"
      viewBox="0 0 32 32"
      fill={color}
      stroke="#1E293B"
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "100%" }}
    >
      <path d="M16 2L20.2 11.4L30.5 12.5L22.8 19.5L25 29.6L16 24.5L7 29.6L9.2 19.5L1.5 12.5L11.8 11.4L16 2Z" />
    </svg>
  </Box>
);

/**
 * Hand-drawn thumbtack / pushpin
 */
export const DoodlePin: React.FC<DoodleProps & { size?: number }> = ({
  color = "#E11D48",
  size = 28,
  ...rest
}) => (
  <Box display="inline-block" w={`${size}px`} h={`${size}px`} {...rest}>
    <svg
      aria-hidden="true"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "100%" }}
    >
      <circle cx="16" cy="10" r="7" fill={color} stroke="#0A3D2E" strokeWidth="2" />
      <path d="M16 17V29" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" />
      <ellipse cx="14" cy="8" rx="2.5" ry="1.5" fill="white" opacity="0.6" />
    </svg>
  </Box>
);

/**
 * Hand-drawn playful paperclip
 */
export const DoodlePaperclip: React.FC<DoodleProps & { size?: number }> = ({
  color = "#10B981",
  size = 32,
  ...rest
}) => (
  <Box display="inline-block" w={`${size * 0.6}px`} h={`${size}px`} {...rest}>
    <svg
      aria-hidden="true"
      viewBox="0 0 24 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "100%" }}
    >
      <path
        d="M16 10V28C16 31.3 13.3 34 10 34C6.7 34 4 31.3 4 28V8C4 4.7 6.7 2 10 2C13.3 2 16 4.7 16 8V25C16 26.7 14.7 28 13 28C11.3 28 10 26.7 10 25V11"
        stroke={color}
        strokeWidth="2.8"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

/**
 * Washi-tape strip with jagged edges for holding notes/cards
 */
export const WashiTape: React.FC<
  DoodleProps & {
    angle?: number;
    tapeColor?: string;
    label?: string;
  }
> = ({
  angle = -3,
  tapeColor = "#FEF08A",
  label,
  width = "120px",
  height = "28px",
  ...rest
}) => (
  <Box
    position="absolute"
    w={width}
    h={height}
    style={{
      transform: `rotate(${angle}deg)`,
      transformOrigin: "center",
    }}
    bg={tapeColor}
    opacity={0.94}
    boxShadow="0 1px 3px rgba(0,0,0,0.12)"
    display="flex"
    alignItems="center"
    justifyContent="center"
    zIndex={2}
    clipPath="polygon(0% 0%, 5% 40%, 0% 100%, 100% 100%, 95% 45%, 100% 0%)"
    {...rest}
  >
    {label && (
      <Box
        className="font-handwriting"
        fontSize="sm"
        fontWeight="bold"
        color="#78350F"
        letterSpacing="wide"
      >
        {label}
      </Box>
    )}
  </Box>
);

/**
 * Sticky note component for testimonials, tips, or highlights
 */
export const StickyNote: React.FC<
  BoxProps & {
    noteColor?: "yellow" | "pink" | "green" | "blue";
    rotation?: number;
    pinColor?: string;
  }
> = ({
  children,
  noteColor = "yellow",
  rotation = -1.5,
  pinColor = "#E11D48",
  ...rest
}) => {
  const colors = {
    yellow: { bg: "#FEF9C3", border: "#FDE047", text: "#713F12" },
    pink: { bg: "#FFE4E6", border: "#FDA4AF", text: "#881337" },
    green: { bg: "#DCFCE7", border: "#86EFAC", text: "#14532D" },
    blue: { bg: "#E0F2FE", border: "#7DD3FC", text: "#0369A1" },
  };

  const c = colors[noteColor];

  return (
    <Box
      position="relative"
      bg={c.bg}
      p={5}
      borderRadius="sm"
      border="1px solid"
      borderColor={c.border}
      boxShadow="3px 4px 10px rgba(0,0,0,0.08), 1px 2px 3px rgba(0,0,0,0.06)"
      style={{
        transform: `rotate(${rotation}deg)`,
        transition: "all 0.2s ease",
      }}
      _hover={{
        transform: `rotate(0deg) scale(1.02)`,
        boxShadow: "5px 8px 18px rgba(0,0,0,0.12)",
      }}
      {...rest}
    >
      <Box position="absolute" top="-12px" left="50%" transform="translateX(-50%)" zIndex={2}>
        <DoodlePin color={pinColor} size={24} />
      </Box>
      {children}
    </Box>
  );
};
